import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {pullRequestsOverviewRoute} from './overview-route'
import type {OverviewRoutePayload} from './route-payload-types'
import {SplitPageLayout} from '@primer/react'
import {Description} from '../components/overview/Description'
import {ChangeSummary} from '../components/overview/ChangeSummary'
import {Findings} from '../components/overview/Findings'
import {CatchUp} from '../components/overview/CatchUp'
import {OverviewPanel} from '../components/overview/OverviewPanel'
import {Reviewers} from '../components/overview/reviewers/Reviewers'
import {CollapsiblePanel} from '../components/overview/CollapsiblePanel'
import {
  DockedPanel,
  useDockedPanelActions,
  useIsDockedPanelCompact,
  useIsDockedPanelOpen,
} from '../contexts/DockedPanelContext'
import {useEffect} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'

export function OverviewEntrypoint() {
  const {data: routeData} = useRouteQuery(pullRequestsOverviewRoute, 'mainQuery')

  return <OverviewComponent routeData={routeData} />
}

type OverviewComponentProps = {
  routeData: OverviewRoutePayload
}

function OverviewComponent({routeData}: OverviewComponentProps) {
  const dockedPanelActions = useDockedPanelActions()
  const isDockedPanelOpen = useIsDockedPanelOpen()
  const isCompact = useIsDockedPanelCompact()
  const isOverviewPanelEnabled = isFeatureEnabled('prs_overview_panel')
  const isCatchUpEnabled = isFeatureEnabled('prs_overview_catch_up')

  useEffect(() => {
    if (isOverviewPanelEnabled) {
      dockedPanelActions.openPanel(DockedPanel.Info)
    }
  }, [dockedPanelActions, isOverviewPanelEnabled])

  // Show sidebar when the flag is off, or on compact (mobile) viewports where the
  // docked panel renders as a dialog instead of in-layout side panel content.
  const showMobileSidebar = !(isOverviewPanelEnabled && isDockedPanelOpen) || isCompact

  return (
    <>
      <SplitPageLayout.Content as="div" padding="condensed">
        <div className="d-flex gap-4 flex-column">
          <Description descriptionHtml={routeData.description} />
          {isCatchUpEnabled && <CatchUp />}
          <CollapsiblePanel heading="Reviews">
            <Reviewers />
          </CollapsiblePanel>
          <ChangeSummary changeHighlights={routeData.changeHighlights} />
          <Findings />
        </div>
      </SplitPageLayout.Content>
      {showMobileSidebar && (
        <SplitPageLayout.Pane position="end" sticky divider="none" padding="condensed" width="large">
          <OverviewPanel />
        </SplitPageLayout.Pane>
      )}
    </>
  )
}
